import * as React from 'react';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { Breadcrumb, Heading } from 'vet-bones/design-system/components';

import { Loading } from 'src/ui/components/Loading';
import {
  ORGANIZATION_DASHBOARD_BREADCRUMB_ENDPOINTS,
  ORGANIZATION_DASHBOARD_BREADCRUMB_ORG,
  ORGANIZATION_DASHBOARD_ENDPOINT_HEADING,
} from 'src/ui/constants/testIds';
import { OrganizationEndpointUserTable } from 'src/ui/containers/organization/OrganizationEndpointUserTable';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useListOrganizationConnectionsQuery } from 'src/ui/graph/types';
import {
  useIsOrganizationAdmin,
  useOrganization,
} from 'src/ui/hooks/organization';
import * as copy from 'src/ui/templates/copy';
import { buildDashboardRoute } from 'src/ui/utils/buildRoute';

export const OrganizationEndpoint: React.VFC = () => {
  const history = useHistory();
  const { org, id } = useParams<{ org: string; id: string }>();

  const { organization } = useOrganization(org);
  const isAdmin = useIsOrganizationAdmin(org);

  const {
    data: connectionsData,
    isLoading,
  } = useListOrganizationConnectionsQuery(graphQLClient, { org_domain: org });

  const connection = React.useMemo(
    () =>
      connectionsData?.listConnections?.find(
        (conn) => conn?.index?.toString() === id
      ),
    [connectionsData, id]
  );

  if (isLoading) {
    return <Loading />;
  }

  if (!isAdmin || !connection || connection.usesOrgSso) {
    return <Redirect to={buildDashboardRoute(org, 'connections')} />;
  }

  if (!organization) {
    return null;
  }

  return (
    <div className="organization-dashboard">
      <div className="organization-dashboard-header">
        <div className="organization-dashboard-header-info">
          <Heading
            size="xl"
            testId={ORGANIZATION_DASHBOARD_ENDPOINT_HEADING}
            weight="semibold"
          >
            {organization.name}
          </Heading>
          <Breadcrumb
            items={[
              {
                id: 'org',
                onClick: () => history.push(buildDashboardRoute(org)),
                testId: ORGANIZATION_DASHBOARD_BREADCRUMB_ORG,
                text: organization.name,
              },
              {
                id: 'endpoints',
                onClick: () =>
                  history.push(buildDashboardRoute(org, 'connections')),
                testId: ORGANIZATION_DASHBOARD_BREADCRUMB_ENDPOINTS,
                text:
                  copy.components.organization.dashboard.breadcrumb.endpoints,
              },
              {
                id: 'endpoint',
                text: connection.name,
              },
            ]}
          />
        </div>
      </div>

      <OrganizationEndpointUserTable />
    </div>
  );
};
